import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { useAdminAuth } from "@/context/AdminAuth";

const STAGES = ["New", "Contacted", "Converted", "Closed"];
const ORDER_STATUSES = ["awaiting_payment", "paid", "shipped", "cancelled"];
const money = (n) => `$${Number(n).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "");

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[var(--line)] p-6 bg-[var(--bone)]">
      <div className="overline text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-4xl mt-2" style={accent ? { color: accent } : {}} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
        {value}
      </div>
    </div>
  );
}

function Select({ value, options, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="font-mono text-xs uppercase tracking-wider border border-[var(--line)] bg-[var(--bone)] px-3 py-2 outline-none"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
      ))}
    </select>
  );
}

function Notes({ initial, onSave, testId }) {
  const [val, setVal] = useState(initial || "");
  return (
    <textarea
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => val !== (initial || "") && onSave(val)}
      placeholder="Add notes…"
      rows={2}
      className="w-full text-sm font-body border border-[var(--line)] bg-[var(--bone)] p-2 outline-none focus:border-[var(--ink)] resize-none"
      data-testid={testId}
    />
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [consults, setConsults] = useState([]);
  const [wholesale, setWholesale] = useState([]);
  const [glp1, setGlp1] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [glp1Query, setGlp1Query] = useState("");
  const [consultQuery, setConsultQuery] = useState("");
  const [wholesaleQuery, setWholesaleQuery] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const [s, o, c, w, g, d] = await Promise.all([
      adminApi.get("/admin/stats"),
      adminApi.get("/admin/orders"),
      adminApi.get("/admin/consults"),
      adminApi.get("/admin/wholesale"),
      adminApi.get("/admin/glp1"),
      adminApi.get("/admin/wholesale-downloads"),
    ]);
    setStats(s.data);
    setOrders(o.data);
    setConsults(c.data);
    setWholesale(w.data);
    setGlp1(g.data);
    setDownloads(d.data);
  }, []);

  useEffect(() => {
    if (admin === null) navigate("/admin/login");
    if (admin) load();
  }, [admin, load, navigate]);

  const setOrderStatus = async (ref, status) => {
    await adminApi.patch(`/admin/orders/${ref}`, { status });
    load();
  };
  const updateConsult = async (id, patch) => {
    await adminApi.patch(`/admin/consults/${id}`, patch);
    setConsults((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    load();
  };
  const updateWholesale = async (id, patch) => {
    await adminApi.patch(`/admin/wholesale/${id}`, patch);
    setWholesale((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };
  const updateGlp1 = async (id, patch) => {
    await adminApi.patch(`/admin/glp1/${id}`, patch);
    setGlp1((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };
  const updateDownload = async (id, patch) => {
    await adminApi.patch(`/admin/wholesale-downloads/${id}`, patch);
    setDownloads((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const sendTestCartEmail = async () => {
    setTesting(true);
    setTestMsg("");
    try {
      const { data } = await adminApi.post("/admin/test-cart-email");
      setTestMsg(data.status === "success"
        ? `Sample email sent to ${data.sent_to} — check your inbox.`
        : `Could not send (status: ${data.status}).`);
    } catch (e) {
      setTestMsg("Failed to send test email. Please try again.");
    } finally {
      setTesting(false);
    }
  };

  const q = orderQuery.trim().toLowerCase();
  const filteredOrders = q
    ? orders.filter((o) => {
        const c = o.customer || {};
        return [c.name, c.email, c.zip, o.reference].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
      })
    : orders;

  const filterLeads = (list, query, getFields) => {
    const s = query.trim().toLowerCase();
    if (!s) return list;
    return list.filter((item) => getFields(item).filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  };
  const filteredGlp1 = filterLeads(glp1, glp1Query, (c) => [c.email, c.compound, c.stage, c.answers?.recommended_dose, c.answers?.prior_dose, c.notes]);
  const filteredConsults = filterLeads(consults, consultQuery, (c) => [c.email, c.stage, c.notes, ...Object.values(c.answers || {})]);
  const filteredWholesale = filterLeads(wholesale, wholesaleQuery, (c) => [c.name, c.email, c.company, c.products, c.message, c.stage, c.notes]);

  const doseHistoryByEmail = {};
  glp1.forEach((c) => {
    if (c.email && c.answers?.recommended_dose) {
      (doseHistoryByEmail[c.email] = doseHistoryByEmail[c.email] || []).push({ date: c.created_at, dose: c.answers.recommended_dose });
    }
  });
  Object.values(doseHistoryByEmail).forEach((arr) => arr.sort((a, b) => new Date(a.date) - new Date(b.date)));

  const TABS = [
    ["overview", "Overview"],
    ["orders", `Orders (${orders.length})`],
    ["glp1", `GLP-1 (${glp1.length})`],
    ["consults", `Consults (${consults.length})`],
    ["wholesale", `Wholesale (${wholesale.length})`],
    ["downloads", `PDF Leads (${downloads.length})`],
  ];

  return (
    <div className="min-h-screen bg-[var(--bone-2)]" style={{ cursor: "auto" }} data-testid="admin-dashboard">
      <header className="bg-[var(--ink)] text-[var(--bone)]">
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <span className="font-display text-2xl">Balance<span className="text-[var(--terra)]">.</span> <span className="overline text-white/40 ml-2">CRM</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="overline text-white/50 hidden sm:block">{admin?.email}</span>
            <a href="/" className="overline text-white/60 hover:text-white">Store ↗</a>
            <button onClick={logout} className="btn-ink !bg-[var(--bone)] !text-[var(--ink)] px-5 py-2.5 rounded-full" data-testid="admin-logout">Log out</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-10 border-b border-[var(--line)] pb-4">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`overline px-4 py-2.5 rounded-full transition-colors ${tab === k ? "bg-[var(--ink)] text-[var(--bone)]" : "text-[var(--ink-soft)] hover:bg-black/5"}`}
              data-testid={`tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Total orders" value={stats.orders_total} />
              <Stat label="Awaiting payment" value={stats.awaiting_payment} accent="#bd7a5e" />
              <Stat label="Paid" value={stats.paid} />
              <Stat label="Shipped" value={stats.shipped} />
              <Stat label="Revenue" value={money(stats.revenue)} accent="#1f9e96" />
              <Stat label="Consult leads" value={stats.consults} />
              <Stat label="Wholesale leads" value={stats.wholesale} />
              <Stat label="PDF downloads" value={stats.downloads} accent="#1f9e96" />
              <Stat label="GLP-1 leads" value={stats.glp1} accent="#bd7a5e" />
            </div>
            <div className="mt-8 border border-[var(--line)] bg-[var(--bone)] p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="font-display text-xl">Preview the abandoned-cart email</div>
                <div className="font-body text-sm text-[var(--ink-soft)] mt-1">Sends a sample "your bag is waiting" email to {admin?.email} so you can see what shoppers receive.</div>
                {testMsg && <div className="font-body text-sm text-[#1f9e96] mt-2" data-testid="test-email-msg">{testMsg}</div>}
              </div>
              <button
                onClick={sendTestCartEmail}
                disabled={testing}
                className="btn-ink px-6 py-3 rounded-full whitespace-nowrap disabled:opacity-40"
                data-testid="send-test-email-btn"
              >
                {testing ? "Sending…" : "Send me a sample"}
              </button>
            </div>
          </>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="orders-list">
            <input
              type="text"
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              placeholder="Search by name, email, ZIP or reference…"
              className="w-full font-body text-sm border border-[var(--line)] bg-[var(--bone)] px-4 py-3 outline-none focus:border-[var(--ink)] mb-2"
              data-testid="order-search"
            />
            {filteredOrders.length === 0 && <p className="font-body text-[var(--ink-soft)]">{orders.length === 0 ? "No orders yet." : "No orders match your search."}</p>}
            {filteredOrders.map((o) => (
              <div key={o.reference} className="bg-[var(--bone)] border border-[var(--line)] p-5 grid md:grid-cols-[1fr_1fr_auto] gap-4 items-start" data-testid={`order-row-${o.reference}`}>
                <div>
                  <div className="font-display text-2xl">{o.reference}</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">{fmtDate(o.created_at)}</div>
                  <div className="font-body text-sm mt-2">{o.customer.name} · {o.customer.email}</div>
                  {o.customer.address && (
                    <div className="font-body text-xs text-[var(--ink-soft)]">
                      {[o.customer.address, o.customer.apt, [o.customer.city, o.customer.state].filter(Boolean).join(", "), o.customer.zip].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="font-body text-sm text-[var(--ink-soft)]">
                  {o.items.map((it, idx) => (<div key={`${o.id}-${it.id}-${idx}`}>{it.qty}× {it.name} {it.variant ? `(${it.variant})` : ""} — {money(it.price * it.qty)}</div>))}
                  <div className="text-xs mt-1">Shipping: {money(o.shipping ?? 8.99)}</div>
                  <div className="font-display text-xl text-[var(--ink)] mt-2">{money(o.total)} · <span className="uppercase text-xs font-mono">{o.payment_method}</span></div>
                </div>
                <Select value={o.status} options={ORDER_STATUSES} onChange={(v) => setOrderStatus(o.reference, v)} testId={`order-status-${o.reference}`} />
              </div>
            ))}
          </div>
        )}

        {tab === "glp1" && (
          <div className="space-y-3" data-testid="glp1-list">
            <input
              type="text"
              value={glp1Query}
              onChange={(e) => setGlp1Query(e.target.value)}
              placeholder="Search by email, compound, dose or stage…"
              className="w-full font-body text-sm border border-[var(--line)] bg-[var(--bone)] px-4 py-3 outline-none focus:border-[var(--ink)] mb-2"
              data-testid="glp1-search"
            />
            {filteredGlp1.length === 0 && <p className="font-body text-[var(--ink-soft)]">{glp1.length === 0 ? "No GLP-1 questionnaires yet." : "No leads match your search."}</p>}
            {filteredGlp1.map((c) => (
              <div key={c.id} className="bg-[var(--bone)] border border-[var(--line)] p-5 grid md:grid-cols-[1.2fr_1fr_auto] gap-4 items-start" data-testid={`glp1-row-${c.id}`}>
                <div>
                  <div className="font-display text-xl">{c.compound} · <span className={c.eligible ? "text-[#1f9e96]" : "text-[var(--terra)]"}>{c.eligible ? "Eligible" : "Not a match"}</span></div>
                  {c.answers?.recommended_dose && (
                    <div className="inline-block mt-1 bg-[var(--terra)] text-white font-mono text-xs tracking-wide px-3 py-1 rounded-full" data-testid={`glp1-dose-${c.id}`}>
                      SHIP: {c.answers.recommended_dose}
                    </div>
                  )}
                  {c.answers?.step_up === "Yes, please adjust it" && (
                    <div className="inline-block mt-1 ml-2 bg-[#b45309] text-white font-mono text-xs tracking-wide px-3 py-1 rounded-full" data-testid={`glp1-stepup-${c.id}`}>
                      ⬆ STEP-UP REQUESTED
                    </div>
                  )}
                  {c.email && doseHistoryByEmail[c.email]?.length > 1 && (
                    <div className="mt-2 text-xs font-body" data-testid={`glp1-dose-history-${c.id}`}>
                      <span className="overline text-[var(--ink-soft)]">Titration: </span>
                      {doseHistoryByEmail[c.email].map((h, i) => (
                        <span key={i} className="text-[var(--ink)]">
                          {i > 0 ? " → " : ""}<b>{h.dose}</b>
                          <span className="text-[var(--ink-soft)]"> ({new Date(h.date).toLocaleDateString()})</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="font-body text-sm mt-1">{c.email || "No email"}</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">{fmtDate(c.created_at)}</div>
                  <div className="font-body text-xs text-[var(--ink-soft)] mt-2">
                    {Object.entries(c.answers || {}).map(([k, v]) => (<div key={k}><b>{k}:</b> {String(v)}</div>))}
                  </div>
                </div>
                <Notes initial={c.notes} onSave={(notes) => updateGlp1(c.id, { notes })} testId={`glp1-notes-${c.id}`} />
                <Select value={c.stage || "New"} options={STAGES} onChange={(v) => updateGlp1(c.id, { stage: v })} testId={`glp1-stage-${c.id}`} />
              </div>
            ))}
          </div>
        )}

        {tab === "consults" && (
          <div className="space-y-3" data-testid="consults-list">
            <input
              type="text"
              value={consultQuery}
              onChange={(e) => setConsultQuery(e.target.value)}
              placeholder="Search by email, stage or answers…"
              className="w-full font-body text-sm border border-[var(--line)] bg-[var(--bone)] px-4 py-3 outline-none focus:border-[var(--ink)] mb-2"
              data-testid="consult-search"
            />
            {filteredConsults.length === 0 && <p className="font-body text-[var(--ink-soft)]">{consults.length === 0 ? "No consults yet." : "No leads match your search."}</p>}
            {filteredConsults.map((c) => (
              <div key={c.id} className="bg-[var(--bone)] border border-[var(--line)] p-5 grid md:grid-cols-[1.2fr_1fr_auto] gap-4 items-start" data-testid={`consult-row-${c.id}`}>
                <div>
                  <div className="font-body text-sm">{c.email || "No email"}</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">{fmtDate(c.created_at)}</div>
                  <div className="font-body text-xs text-[var(--ink-soft)] mt-2">
                    {Object.entries(c.answers || {}).map(([k, v]) => (<div key={k}><b>{k}:</b> {String(v)}</div>))}
                  </div>
                </div>
                <Notes initial={c.notes} onSave={(notes) => updateConsult(c.id, { notes })} testId={`consult-notes-${c.id}`} />
                <Select value={c.stage || "New"} options={STAGES} onChange={(v) => updateConsult(c.id, { stage: v })} testId={`consult-stage-${c.id}`} />
              </div>
            ))}
          </div>
        )}

        {tab === "wholesale" && (
          <div className="space-y-3" data-testid="wholesale-list">
            <input
              type="text"
              value={wholesaleQuery}
              onChange={(e) => setWholesaleQuery(e.target.value)}
              placeholder="Search by name, email, company or products…"
              className="w-full font-body text-sm border border-[var(--line)] bg-[var(--bone)] px-4 py-3 outline-none focus:border-[var(--ink)] mb-2"
              data-testid="wholesale-search"
            />
            {filteredWholesale.length === 0 && <p className="font-body text-[var(--ink-soft)]">{wholesale.length === 0 ? "No wholesale inquiries yet." : "No leads match your search."}</p>}
            {filteredWholesale.map((c) => (
              <div key={c.id} className="bg-[var(--bone)] border border-[var(--line)] p-5 grid md:grid-cols-[1.2fr_1fr_auto] gap-4 items-start" data-testid={`wholesale-row-${c.id}`}>
                <div>
                  <div className="font-display text-xl">{c.name}</div>
                  <div className="font-body text-sm">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">{fmtDate(c.created_at)}</div>
                  {c.products && <div className="font-body text-xs text-[var(--ink-soft)] mt-2"><b>Interested:</b> {c.products}</div>}
                  {c.message && <div className="font-body text-xs text-[var(--ink-soft)] mt-1">{c.message}</div>}
                </div>
                <Notes initial={c.notes} onSave={(notes) => updateWholesale(c.id, { notes })} testId={`wholesale-notes-${c.id}`} />
                <Select value={c.stage || "New"} options={STAGES} onChange={(v) => updateWholesale(c.id, { stage: v })} testId={`wholesale-stage-${c.id}`} />
              </div>
            ))}
          </div>
        )}

        {tab === "downloads" && (
          <div className="space-y-3" data-testid="downloads-list">
            <p className="font-body text-sm text-[var(--ink-soft)] mb-2">Everyone who downloaded the wholesale price sheet — follow up to close the sale.</p>
            {downloads.length === 0 && <p className="font-body text-[var(--ink-soft)]">No price-sheet downloads yet.</p>}
            {downloads.map((c) => (
              <div key={c.id} className="bg-[var(--bone)] border border-[var(--line)] p-5 grid md:grid-cols-[1.2fr_1fr_auto] gap-4 items-start" data-testid={`download-row-${c.id}`}>
                <div>
                  <div className="font-display text-xl">{c.email}</div>
                  {c.company && <div className="font-body text-sm text-[var(--ink-soft)]">{c.company}</div>}
                  <div className="overline text-[var(--ink-soft)] mt-1">{fmtDate(c.created_at)}{c.downloads > 1 ? ` · ${c.downloads} downloads` : ""}</div>
                  <a href={`mailto:${c.email}?subject=Your%20Balance%20wholesale%20pricing`} className="overline underline text-[var(--terra)] mt-2 inline-block" data-testid={`download-email-${c.id}`}>Email this partner ↗</a>
                </div>
                <Notes initial={c.notes} onSave={(notes) => updateDownload(c.id, { notes })} testId={`download-notes-${c.id}`} />
                <Select value={c.stage || "New"} options={STAGES} onChange={(v) => updateDownload(c.id, { stage: v })} testId={`download-stage-${c.id}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
